import { useState, useEffect, useRef } from 'react';
import { getOrGenerateArtifact, logArtifactInteraction, flagArtifact, getGradeBand } from '../../services/conceptPlus1Service';
import type { ArtifactBlob } from '../../services/conceptPlus1Service';
import { logMasteryEvent, getPhaseAttemptCounts } from '../../services/masteryService';
import type { ConfidenceLevel } from '../../services/masteryService';
import { markAssignmentComplete } from '../../services/assignmentService';
import type { AssignmentContext } from '../../services/assignmentService';

function fitToFrame(html: string, zoom = 1): string {
  const meta = '<meta name="viewport" content="width=device-width, initial-scale=1">';
  const css = `<style>html{height:100%;margin:0;padding:0;display:flex;align-items:center;justify-content:center;}body{min-height:unset;margin:0;padding:0;overflow-y:auto;-webkit-overflow-scrolling:touch;box-sizing:border-box;zoom:${zoom}}button,input,select,a,[onclick],[role="button"]{cursor:pointer}</style>`;
  const inject = meta + css;
  if (html.includes('</head>')) return html.replace('</head>', inject + '</head>');
  if (html.includes('<head>')) return html.replace('<head>', '<head>' + inject);
  return inject + html;
}

type Step = 'loading' | 'predict' | 'interact' | 'reflect' | 'confidence' | 'result';

const STEP_LABELS: Record<Exclude<Step, 'loading'>, string> = {
  predict:    'Predict',
  interact:   'Simulate',
  reflect:    'Reflect',
  confidence: 'Confidence',
  result:     'Done',
};
const STEP_ORDER: Exclude<Step, 'loading'>[] = ['predict', 'interact', 'reflect', 'confidence', 'result'];

const CONFIDENCE_EMOJIS: { level: ConfidenceLevel; emoji: string; title: string }[] = [
  { level: 'low',    emoji: '😕', title: 'Not confident' },
  { level: 'medium', emoji: '🤔', title: 'Somewhat confident' },
  { level: 'high',   emoji: '😊', title: 'Very confident' },
];

function ConfidenceTap({
  label, tapped, onTap,
}: {
  label: string; tapped: ConfidenceLevel | null; onTap: (c: ConfidenceLevel) => void;
}) {
  return (
    <div className="flex items-center gap-2 py-0.5">
      <span className="text-xs text-gray-400">{label}</span>
      <div className="flex items-center gap-1">
        {CONFIDENCE_EMOJIS.map(({ level, emoji, title }) => (
          <button
            key={level}
            onClick={() => onTap(level)}
            disabled={!!tapped}
            title={title}
            className={`text-base leading-none transition-all hover:scale-125 active:scale-95 ${
              tapped && tapped !== level ? 'opacity-25' : ''
            } ${tapped === level ? 'scale-110' : ''}`}
          >
            {emoji}
          </button>
        ))}
      </div>
      {tapped && <span className="text-xs text-gray-300">✓</span>}
    </div>
  );
}

interface Props {
  conceptId: number;
  conceptName: string;
  subjectName: string;
  gradeLevel: number;
  studentId: string;
  onComplete?: () => void;
  fullscreen?: boolean;
  assignmentContext?: AssignmentContext;
}

export default function TryItPanel({
  conceptId, conceptName, subjectName, gradeLevel, studentId, onComplete, fullscreen = false,
  assignmentContext,
}: Props) {
  const [step, setStep]             = useState<Step>('loading');
  const [artifact, setArtifact]     = useState<ArtifactBlob | null>(null);
  const [error, setError]           = useState<string | null>(null);
  const [predictIdx, setPredictIdx] = useState<number | null>(null);
  const [reflectIdx, setReflectIdx] = useState<number | null>(null);
  const [hasInteracted, setHasInteracted]         = useState(false);
  const [hintDismissed, setHintDismissed]         = useState(false);
  const [reflectConfidence, setReflectConfidence] = useState<ConfidenceLevel | null>(null);
  const [priorAttempts, setPriorAttempts]         = useState<{ predict: number; reflect: number } | null>(null);
  const [simZoom, setSimZoom]                     = useState(() => {
    const h = window.innerHeight;
    if (h < 700)  return 0.70;
    if (h < 850)  return 0.82;
    if (h < 1000) return 0.90;
    return 1;
  });
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const assignmentFiredRef = useRef(false);

  const handleSimZoom = (delta: number) => {
    setSimZoom(z => Math.min(1.5, Math.max(0.4, +(z + delta).toFixed(2))));
    setHasInteracted(false);
  };

  const loadArtifact = () => {
    getOrGenerateArtifact({ conceptId, conceptName, subjectName, gradeLevel })
      .then(res => {
        if (res.error || !res.artifact_json) { setError('Could not load simulation. Try again later.'); return; }
        const a = res.artifact_json;
        if (!a.predict?.question || !a.reflect?.question || !a.artifact_html) {
          setError('Simulation data is incomplete. Please try again later.');
          return;
        }
        setArtifact(a);
        setStep('predict');
      })
      .catch(() => setError('Could not load simulation. Try again later.'));
  };

  useEffect(() => { loadArtifact(); }, [conceptId]);

  useEffect(() => {
    if (studentId) {
      getPhaseAttemptCounts(studentId, conceptId).then(setPriorAttempts);
    }
  }, [studentId, conceptId]);

  useEffect(() => {
    const handler = (e: MessageEvent) => { if (e.data === 'interacted') setHasInteracted(true); };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, []);

  const handlePredict = (idx: number) => { setPredictIdx(idx); setStep('interact'); setHasInteracted(false); };
  const handleSeeResult = () => setStep('reflect');

  const handleReflect = (idx: number) => { setReflectIdx(idx); setStep('confidence'); };

  const handleReflectConfidence = (confidence: ConfidenceLevel) => {
    if (reflectConfidence) return;
    setReflectConfidence(confidence);
    if (!studentId || reflectIdx === null || !artifact) return;
    logMasteryEvent({
      studentId, conceptId, eventType: 'artifact_reflect',
      correct: reflectIdx === artifact.reflect.correct_index,
      confidence,
    });
  };

  const handleReveal = async () => {
    if (artifact && predictIdx !== null && reflectIdx !== null) {
      logMasteryEvent({
        studentId, conceptId, eventType: 'artifact_predict',
        correct: predictIdx === artifact.predict.correct_index,
      });
      await logArtifactInteraction({
        studentId, conceptId,
        predictCorrect: predictIdx === artifact.predict.correct_index,
        reflectCorrect: reflectIdx === artifact.reflect.correct_index,
      });
    }
    if (assignmentContext?.type === 'explore' && !assignmentFiredRef.current && studentId) {
      assignmentFiredRef.current = true;
      markAssignmentComplete(assignmentContext.id, studentId, 100, 1).catch(() => {});
    }
    setStep('result');
  };

  const handleDone = () => {
    if (onComplete) {
      onComplete();
    } else {
      setStep('loading');
      setArtifact(null);
      setError(null);
      setPredictIdx(null);
      setReflectIdx(null);
      setReflectConfidence(null);
      setHasInteracted(false);
      loadArtifact();
    }
  };

  const handleFlag = async () => {
    await flagArtifact(conceptId, getGradeBand(gradeLevel));
    setStep('loading');
    setArtifact(null);
    setError(null);
    setPredictIdx(null);
    setReflectIdx(null);
    setReflectConfidence(null);
    getOrGenerateArtifact({ conceptId, conceptName, subjectName, gradeLevel })
      .then(res => {
        if (res.error || !res.artifact_json) { setError('Regeneration failed.'); return; }
        const a = res.artifact_json;
        if (!a.predict?.question || !a.reflect?.question || !a.artifact_html) { setError('Regeneration failed.'); return; }
        setArtifact(a);
        setStep('predict');
      });
  };

  // ── loading / error states ──────────────────────────────────────────────────

  if (error) return <div className="text-center py-8 text-gray-500 text-sm"><p>{error}</p></div>;

  if (step === 'loading') return (
    <div className={`flex items-center justify-center ${fullscreen ? 'flex-1' : 'py-10'}`}>
      <div className="w-6 h-6 border-2 border-coral-warm border-t-transparent rounded-full animate-spin" />
      <span className="ml-3 text-sm text-gray-500">Preparing simulation…</span>
    </div>
  );

  if (!artifact) return null;

  const stepIndex = STEP_ORDER.indexOf(step as Exclude<Step, 'loading'>);

  const priorPredictCount = priorAttempts?.predict ?? 0;
  const attemptLabel = priorPredictCount > 0 ? `Attempt ${priorPredictCount + 1}` : null;

  const stepIndicator = (
    <div className={`flex items-center justify-between text-xs text-gray-400 ${
      fullscreen ? 'flex-none px-4 py-2.5 border-b border-gray-100' : ''
    }`}>
      <div className="flex items-center gap-1.5">
        {STEP_ORDER.map((s, i) => (
          <div key={s} className="flex items-center gap-1">
            <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold ${
              step === s ? 'bg-coral-warm text-white' :
              stepIndex > i ? 'bg-orange-200 text-coral-warm' : 'bg-gray-100 text-gray-400'
            }`}>{i + 1}</div>
            {i < 4 && <div className="w-3 h-px bg-gray-200" />}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        {attemptLabel && (
          <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-semibold">
            {attemptLabel}
          </span>
        )}
        <span className="font-medium text-gray-500 capitalize">
          {STEP_LABELS[step as Exclude<Step, 'loading'>] ?? ''}
        </span>
      </div>
    </div>
  );

  // ── fullscreen layout ──────────────────────────────────────────────────────

  if (fullscreen) {
    return (
      <div className="flex flex-col h-full">
        {stepIndicator}

        {/* PREDICT */}
        {step === 'predict' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <p className="font-medium text-gray-800 text-sm">{artifact.predict.question}</p>
              <div className="space-y-2">
                {artifact.predict.options.map((opt, i) => (
                  <button key={i} onClick={() => handlePredict(i)}
                    className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-coral-warm hover:bg-orange-50 text-sm transition-colors">
                    {opt}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex-none px-4 pb-6 pt-3 border-t border-gray-100">
              <p className="text-xs text-center text-gray-400">Tap an answer to continue</p>
            </div>
          </>
        )}

        {/* INTERACT */}
        {step === 'interact' && (
          <>
            <div className="relative flex-1 overflow-auto">
              <iframe
                ref={iframeRef}
                srcDoc={fitToFrame(artifact.artifact_html, simZoom)}
                sandbox="allow-scripts"
                className="w-full h-full"
                style={{ border: 'none' }}
                title="Interactive simulation"
              />

              {/* Zoom controls */}
              <div className="absolute bottom-3 right-3 flex flex-col items-center gap-0.5 bg-white/95 rounded-xl shadow-md border border-gray-200 p-1.5 z-10">
                <button onClick={() => handleSimZoom(+0.1)}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-coral-warm font-bold text-base leading-none">+</button>
                <span className="text-[10px] text-gray-400 font-medium tabular-nums">{Math.round(simZoom * 100)}%</span>
                <button onClick={() => handleSimZoom(-0.1)}
                  className="w-7 h-7 flex items-center justify-center text-gray-600 hover:text-coral-warm font-bold text-base leading-none">−</button>
              </div>

              {/* Report button */}
              <button onClick={handleFlag}
                className="absolute top-2 right-2 text-[10px] text-gray-400 hover:text-red-500 bg-white/80 px-2 py-0.5 rounded-full border border-gray-200 z-10"
                title="Report a problem">
                ⚑ Report
              </button>

            </div>

            {/* Interaction hint — below iframe, never overlapping */}
            {!hasInteracted && !hintDismissed && (
              <div className="flex-none flex items-center justify-between gap-2 px-3 py-2 bg-orange-50 border-t border-orange-100">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-lg flex-shrink-0">👆</span>
                  <div className="min-w-0">
                    <p className="text-xs font-semibold text-coral-warm leading-tight">Tap the simulation above to interact</p>
                    <p className="text-[10px] text-orange-300 leading-tight hidden sm:block">Use +/− to zoom · scroll inside for more</p>
                  </div>
                </div>
                <button
                  onClick={() => setHintDismissed(true)}
                  className="flex-shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-orange-300 hover:text-orange-600 hover:bg-orange-100 transition-colors text-sm font-bold"
                  aria-label="Dismiss hint"
                >
                  ×
                </button>
              </div>
            )}

            <div className="flex-none px-4 pb-6 pt-3 border-t border-gray-100">
              <button
                onClick={handleSeeResult}
                disabled={!hasInteracted}
                className={`w-full py-3 rounded-xl text-sm font-medium transition-colors ${
                  hasInteracted
                    ? 'bg-coral-warm text-white hover:bg-coral-warm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {hasInteracted ? 'See Result →' : 'Interact with the simulation first'}
              </button>
            </div>
          </>
        )}

        {/* REFLECT */}
        {step === 'reflect' && (
          <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
            <p className="font-medium text-gray-800 text-sm">{artifact.reflect.question}</p>
            <div className="space-y-2">
              {artifact.reflect.options.map((opt, i) => (
                <button key={i} onClick={() => handleReflect(i)}
                  className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-coral-warm hover:bg-orange-50 text-sm transition-colors">
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* CONFIDENCE */}
        {step === 'confidence' && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4">
              <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Before we reveal your result…</p>
              <ConfidenceTap label="How confident are you now?" tapped={reflectConfidence} onTap={handleReflectConfidence} />
            </div>
            <div className="flex-none px-4 pb-6 pt-3 border-t border-gray-100">
              <button
                onClick={handleReveal}
                disabled={!reflectConfidence}
                className={`w-full py-3 rounded-xl text-sm font-medium transition-colors ${
                  reflectConfidence
                    ? 'bg-coral-warm text-white hover:bg-coral-warm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                Reveal Answer →
              </button>
            </div>
          </>
        )}

        {/* RESULT */}
        {step === 'result' && reflectIdx !== null && (
          <>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3">
              <div className={`p-4 rounded-xl text-sm ${
                reflectIdx === artifact.reflect.correct_index
                  ? 'bg-green-50 border border-green-200 text-green-800'
                  : 'bg-orange-50 border border-orange-200 text-orange-800'
              }`}>
                <span className="font-semibold mr-1">
                  {reflectIdx === artifact.reflect.correct_index ? '✓ Correct!' : '✗ Not quite.'}
                </span>
                {artifact.reflect.explanation}
              </div>
              {predictIdx !== null && (
                <p className="text-xs text-gray-400">
                  Prediction: {predictIdx === artifact.predict.correct_index ? '✓ correct' : '✗ incorrect'}
                </p>
              )}
            </div>
            <div className="flex-none px-4 pb-6 pt-3 border-t border-gray-100">
              <button
                onClick={handleDone}
                className="w-full py-3 rounded-xl bg-coral-warm text-white text-sm font-medium hover:bg-coral-warm transition-colors"
              >
                Done ✓
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ── embedded layout (non-fullscreen) ──────────────────────────────────────

  return (
    <div className="space-y-3">
      {stepIndicator}

      {step === 'predict' && (
        <div className="space-y-3">
          <p className="font-medium text-gray-800 text-sm">{artifact.predict.question}</p>
          <div className="space-y-2">
            {artifact.predict.options.map((opt, i) => (
              <button key={i} onClick={() => handlePredict(i)}
                className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-coral-warm hover:bg-orange-50 text-sm transition-colors">
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'interact' && (
        <div className="space-y-0">
          <div className="relative rounded-xl overflow-hidden border border-gray-200 bg-white">
            <iframe
              ref={iframeRef}
              srcDoc={artifact.artifact_html}
              sandbox="allow-scripts"
              className="w-full h-64 sm:h-[460px]"
              style={{ border: 'none' }}
              title="Interactive simulation"
            />
            <button onClick={handleFlag}
              className="absolute top-2 right-2 text-[10px] text-gray-400 hover:text-red-500 bg-white/80 px-2 py-0.5 rounded-full border border-gray-200"
              title="Report a problem">
              ⚑ Report
            </button>
          </div>
          <div className="sticky bottom-3 z-10 pt-2">
            <button
              onClick={handleSeeResult}
              disabled={!hasInteracted}
              className={`w-full py-3 rounded-lg text-sm font-medium transition-colors shadow-md ${
                hasInteracted ? 'bg-coral-warm text-white hover:bg-coral-warm' : 'bg-gray-100 text-gray-400 cursor-not-allowed'
              }`}
            >
              {hasInteracted ? 'See Result →' : 'Interact with the simulation first'}
            </button>
          </div>
        </div>
      )}

      {step === 'reflect' && (
        <div className="space-y-3">
          <p className="font-medium text-gray-800 text-sm">{artifact.reflect.question}</p>
          <div className="space-y-2">
            {artifact.reflect.options.map((opt, i) => (
              <button key={i} onClick={() => handleReflect(i)}
                className="w-full text-left px-4 py-2.5 rounded-lg border border-gray-200 bg-white hover:border-coral-warm hover:bg-orange-50 text-sm transition-colors">
                {opt}
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'confidence' && (
        <div className="space-y-3">
          <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Before we reveal your result…</p>
          <ConfidenceTap label="How confident are you now?" tapped={reflectConfidence} onTap={handleReflectConfidence} />
          <button
            onClick={handleReveal}
            disabled={!reflectConfidence}
            className={`w-full py-2.5 rounded-lg text-sm font-medium transition-colors ${
              reflectConfidence
                ? 'bg-coral-warm text-white hover:bg-coral-warm'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            Reveal Answer →
          </button>
        </div>
      )}

      {step === 'result' && reflectIdx !== null && (
        <div className="space-y-3">
          <div className={`p-3 rounded-lg text-sm ${
            reflectIdx === artifact.reflect.correct_index
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-orange-50 border border-orange-200 text-orange-800'
          }`}>
            <span className="font-semibold mr-1">
              {reflectIdx === artifact.reflect.correct_index ? '✓ Correct!' : '✗ Not quite.'}
            </span>
            {artifact.reflect.explanation}
          </div>
          {predictIdx !== null && (
            <div className="text-xs text-gray-400">
              Prediction: {predictIdx === artifact.predict.correct_index ? '✓ correct' : '✗ incorrect'}
            </div>
          )}
          <button
            onClick={handleDone}
            className="w-full py-2.5 rounded-lg bg-coral-warm text-white text-sm font-medium hover:bg-coral-warm transition-colors"
          >
            Done ✓
          </button>
        </div>
      )}
    </div>
  );
}
